type TypographyUsageWithSize =
  | 'Display_46'
  | 'Display_40'
  | 'Display_36'
  | 'Display_28'
  | 'Heading_24'
  | 'Heading_20'
  | 'Heading_18'
  | 'Heading_17'
  | 'Body_15'
  | 'Body_14'
  | 'Body_13'
  | 'Body_12'
  | 'Caption_11'
  | 'Caption_10';
type DeprecatedTypographyUsageWithSize = 'Heading_15';
type TypographyUsageWithSizeIncludingDeprecated = TypographyUsageWithSize | DeprecatedTypographyUsageWithSize;
type TypographyWeight = 'Bold' | 'SemiBold' | 'Medium' | 'Regular';
export type NonDeprecatedTypography = `${TypographyUsageWithSize}_${TypographyWeight}`;
/** @deprecated Use NonDeprecatedTypography variants (Body_15_* instead of Heading_15_*) */
export type DeprecatedTypography = `${DeprecatedTypographyUsageWithSize}_${TypographyWeight}`;
export type Typography = NonDeprecatedTypography | DeprecatedTypography;

type DeprecatedTextStyles = {
  /** @deprecated Use Body_15_Bold instead */
  Heading_15_Bold: string;
  /** @deprecated Use Body_15_SemiBold instead */
  Heading_15_SemiBold: string;
  /** @deprecated Use Body_15_Medium instead */
  Heading_15_Medium: string;
  /** @deprecated Use Body_15_Regular instead */
  Heading_15_Regular: string;
};

export const text_style_specs: Record<
  TypographyUsageWithSizeIncludingDeprecated,
  { fontSize: number; lineHeight: number; letterSpacing?: string }
> = {
  Display_46: { fontSize: 46, lineHeight: 60, letterSpacing: '-1.15px' },
  Display_40: { fontSize: 40, lineHeight: 52, letterSpacing: '-1px' },
  Display_36: { fontSize: 36, lineHeight: 46, letterSpacing: '-0.9px' },
  Display_28: { fontSize: 28, lineHeight: 36, letterSpacing: '-0.7px' },
  Heading_24: { fontSize: 24, lineHeight: 32 },
  Heading_20: { fontSize: 20, lineHeight: 28 },
  Heading_18: { fontSize: 18, lineHeight: 26 },
  Heading_17: { fontSize: 17, lineHeight: 24 },
  Body_15: { fontSize: 15, lineHeight: 22 },
  Heading_15: { fontSize: 15, lineHeight: 22 },
  Body_14: { fontSize: 14, lineHeight: 20 },
  Body_13: { fontSize: 13, lineHeight: 20 },
  Body_12: { fontSize: 12, lineHeight: 18 },
  Caption_11: { fontSize: 11, lineHeight: 16 },
  Caption_10: { fontSize: 10, lineHeight: 14 },
};

function buildTextStyleWithSize({
  fontSize,
  lineHeight,
  letterSpacing,
}: (typeof text_style_specs)[TypographyUsageWithSizeIncludingDeprecated]) {
  return [
    '',
    `    font-size: ${fontSize}px;`,
    `    line-height: ${lineHeight}px;`,
    ...(letterSpacing ? [`    letter-spacing: ${letterSpacing};`] : []),
    '  ',
  ].join('\n');
}

const text_styles_with_size: Record<TypographyUsageWithSizeIncludingDeprecated, string> = (
  Object.keys(text_style_specs) as Array<keyof typeof text_style_specs>
).reduce((acc, key) => {
  acc[key] = buildTextStyleWithSize(text_style_specs[key]);
  return acc;
}, {} as Record<TypographyUsageWithSizeIncludingDeprecated, string>);

function getFontWeight(weight: TypographyWeight) {
  switch (weight) {
    case 'Bold':
      return 700;
    case 'SemiBold':
      return 600;
    case 'Medium':
      return 500;
    case 'Regular':
    default:
      return 400;
  }
}

function getTextStyle(usage_with_size: TypographyUsageWithSizeIncludingDeprecated, weight: TypographyWeight) {
  return `
    ${text_styles_with_size[usage_with_size]}
    font-weight: ${getFontWeight(weight)};
  `;
}

export const text_styles: Record<NonDeprecatedTypography, string> & DeprecatedTextStyles = {
  Display_46_Bold: getTextStyle('Display_46', 'Bold'),
  Display_46_SemiBold: getTextStyle('Display_46', 'SemiBold'),
  Display_46_Medium: getTextStyle('Display_46', 'Medium'),
  Display_46_Regular: getTextStyle('Display_46', 'Regular'),
  Display_40_Bold: getTextStyle('Display_40', 'Bold'),
  Display_40_SemiBold: getTextStyle('Display_40', 'SemiBold'),
  Display_40_Medium: getTextStyle('Display_40', 'Medium'),
  Display_40_Regular: getTextStyle('Display_40', 'Regular'),
  Display_36_Bold: getTextStyle('Display_36', 'Bold'),
  Display_36_SemiBold: getTextStyle('Display_36', 'SemiBold'),
  Display_36_Medium: getTextStyle('Display_36', 'Medium'),
  Display_36_Regular: getTextStyle('Display_36', 'Regular'),
  Display_28_Bold: getTextStyle('Display_28', 'Bold'),
  Display_28_SemiBold: getTextStyle('Display_28', 'SemiBold'),
  Display_28_Medium: getTextStyle('Display_28', 'Medium'),
  Display_28_Regular: getTextStyle('Display_28', 'Regular'),
  Heading_24_Bold: getTextStyle('Heading_24', 'Bold'),
  Heading_24_SemiBold: getTextStyle('Heading_24', 'SemiBold'),
  Heading_24_Medium: getTextStyle('Heading_24', 'Medium'),
  Heading_24_Regular: getTextStyle('Heading_24', 'Regular'),
  Heading_20_Bold: getTextStyle('Heading_20', 'Bold'),
  Heading_20_SemiBold: getTextStyle('Heading_20', 'SemiBold'),
  Heading_20_Medium: getTextStyle('Heading_20', 'Medium'),
  Heading_20_Regular: getTextStyle('Heading_20', 'Regular'),
  Heading_18_Bold: getTextStyle('Heading_18', 'Bold'),
  Heading_18_SemiBold: getTextStyle('Heading_18', 'SemiBold'),
  Heading_18_Medium: getTextStyle('Heading_18', 'Medium'),
  Heading_18_Regular: getTextStyle('Heading_18', 'Regular'),
  Heading_17_Bold: getTextStyle('Heading_17', 'Bold'),
  Heading_17_SemiBold: getTextStyle('Heading_17', 'SemiBold'),
  Heading_17_Medium: getTextStyle('Heading_17', 'Medium'),
  Heading_17_Regular: getTextStyle('Heading_17', 'Regular'),
  Body_15_Bold: getTextStyle('Body_15', 'Bold'),
  Body_15_SemiBold: getTextStyle('Body_15', 'SemiBold'),
  Body_15_Medium: getTextStyle('Body_15', 'Medium'),
  Body_15_Regular: getTextStyle('Body_15', 'Regular'),
  Heading_15_Bold: getTextStyle('Heading_15', 'Bold'),
  Heading_15_SemiBold: getTextStyle('Heading_15', 'SemiBold'),
  Heading_15_Medium: getTextStyle('Heading_15', 'Medium'),
  Heading_15_Regular: getTextStyle('Heading_15', 'Regular'),
  Body_14_Bold: getTextStyle('Body_14', 'Bold'),
  Body_14_SemiBold: getTextStyle('Body_14', 'SemiBold'),
  Body_14_Medium: getTextStyle('Body_14', 'Medium'),
  Body_14_Regular: getTextStyle('Body_14', 'Regular'),
  Body_13_Bold: getTextStyle('Body_13', 'Bold'),
  Body_13_SemiBold: getTextStyle('Body_13', 'SemiBold'),
  Body_13_Medium: getTextStyle('Body_13', 'Medium'),
  Body_13_Regular: getTextStyle('Body_13', 'Regular'),
  Body_12_Bold: getTextStyle('Body_12', 'Bold'),
  Body_12_SemiBold: getTextStyle('Body_12', 'SemiBold'),
  Body_12_Medium: getTextStyle('Body_12', 'Medium'),
  Body_12_Regular: getTextStyle('Body_12', 'Regular'),
  Caption_11_Bold: getTextStyle('Caption_11', 'Bold'),
  Caption_11_SemiBold: getTextStyle('Caption_11', 'SemiBold'),
  Caption_11_Medium: getTextStyle('Caption_11', 'Medium'),
  Caption_11_Regular: getTextStyle('Caption_11', 'Regular'),
  Caption_10_Bold: getTextStyle('Caption_10', 'Bold'),
  Caption_10_SemiBold: getTextStyle('Caption_10', 'SemiBold'),
  Caption_10_Medium: getTextStyle('Caption_10', 'Medium'),
  Caption_10_Regular: getTextStyle('Caption_10', 'Regular'),
};
